(() => {
class GhProductGallery extends HTMLElement {
  connectedCallback() {
    this.scroller = this.querySelector('.product-media--container');
    this.slides = Array.from(this.querySelectorAll('.product-media--root'));
    this.previousButton = this.querySelector('[data-gh-gallery-previous]');
    this.nextButton = this.querySelector('[data-gh-gallery-next]');
    this.expandButton = this.querySelector('[data-gh-gallery-expand]');
    this.eventName = `theme:product:${this.dataset.id}:variantChanged`;

    this.handleScroll = this.updateButtons.bind(this);
    this.handleVariantChange = this.onVariantChange.bind(this);
    this.handlePrevious = () => this.move(-1);
    this.handleNext = () => this.move(1);
    this.handleExpand = () => this.expandCurrentMedia();

    this.previousButton?.addEventListener('click', this.handlePrevious);
    this.nextButton?.addEventListener('click', this.handleNext);
    this.expandButton?.addEventListener('click', this.handleExpand);
    this.scroller?.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener(this.eventName, this.handleVariantChange);

    requestAnimationFrame(() => {
      const initialSlide = this.querySelector('.product-media--root[data-active="true"]');
      if (initialSlide) this.scrollToSlide(initialSlide, 'auto');
      this.updateButtons();
    });
  }

  disconnectedCallback() {
    this.previousButton?.removeEventListener('click', this.handlePrevious);
    this.nextButton?.removeEventListener('click', this.handleNext);
    this.expandButton?.removeEventListener('click', this.handleExpand);
    this.scroller?.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener(this.eventName, this.handleVariantChange);
  }

  move(direction) {
    if (!this.scroller || this.slides.length < 2) return;

    const currentIndex = this.currentIndex();
    const nextIndex = (currentIndex + direction + this.slides.length) % this.slides.length;
    const wrapped = (direction > 0 && nextIndex === 0) || (direction < 0 && nextIndex === this.slides.length - 1);
    this.scrollToSlide(this.slides[nextIndex], wrapped || this.prefersReducedMotion() ? 'instant' : 'smooth');
  }

  currentIndex() {
    if (!this.scroller || !this.slides.length) return 0;

    const scrollerLeft = this.scroller.getBoundingClientRect().left;

    return this.slides.reduce(
      (closest, slide, index) => {
        const distance = Math.abs(slide.getBoundingClientRect().left - scrollerLeft);
        return distance < closest.distance ? { index, distance } : closest;
      },
      { index: 0, distance: Number.POSITIVE_INFINITY }
    ).index;
  }

  scrollToSlide(slide, behavior = 'smooth') {
    if (!this.scroller || !slide) return;
    const scrollerLeft = this.scroller.getBoundingClientRect().left;
    const slideLeft = slide.getBoundingClientRect().left;
    const targetLeft = this.scroller.scrollLeft + slideLeft - scrollerLeft;
    this.scroller.scrollTo({ left: targetLeft, behavior });
  }

  updateButtons() {
    if (!this.scroller) return;

    const disabled = this.slides.length < 2;
    if (this.previousButton) this.previousButton.disabled = disabled;
    if (this.nextButton) this.nextButton.disabled = disabled;
  }

  onVariantChange(event) {
    const mediaId = event.detail?.variant?.featured_media?.id;
    if (!mediaId) return;

    const target = this.slides.find((slide) => String(slide.dataset.id) === String(mediaId));
    if (target) this.scrollToSlide(target, this.prefersReducedMotion() ? 'auto' : 'smooth');
  }

  expandCurrentMedia() {
    const activeSlide = this.slides[this.currentIndex()];
    const modalTrigger = activeSlide?.querySelector('.product-media--modal');
    modalTrigger?.click();
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

class GhProductOffers extends HTMLElement {
  connectedCallback() {
    this.offers = Array.from(this.querySelectorAll('[data-gh-offer]'));
    this.previousButton = this.querySelector('[data-gh-offer-prev]');
    this.nextButton = this.querySelector('[data-gh-offer-next]');
    this.currentLabel = this.querySelector('[data-gh-offer-current]');
    this.copyStatus = this.querySelector('[data-gh-copy-status]');
    this.index = Math.max(
      0,
      this.offers.findIndex((offer) => offer.getAttribute('aria-hidden') === 'false')
    );

    this.handlePrevious = () => this.show(this.index - 1);
    this.handleNext = () => this.show(this.index + 1);
    this.handleClick = this.onClick.bind(this);

    this.previousButton?.addEventListener('click', this.handlePrevious);
    this.nextButton?.addEventListener('click', this.handleNext);
    this.addEventListener('click', this.handleClick);
    this.show(this.index);
  }

  disconnectedCallback() {
    this.previousButton?.removeEventListener('click', this.handlePrevious);
    this.nextButton?.removeEventListener('click', this.handleNext);
    this.removeEventListener('click', this.handleClick);
  }

  show(requestedIndex) {
    if (!this.offers.length) return;

    this.index = (requestedIndex + this.offers.length) % this.offers.length;
    this.offers.forEach((offer, index) => {
      offer.setAttribute('aria-hidden', String(index !== this.index));
    });
    if (this.currentLabel) this.currentLabel.textContent = String(this.index + 1);
  }

  async onClick(event) {
    const copyButton = event.target.closest('[data-gh-copy-code]');
    if (!copyButton) return;

    const code = copyButton.dataset.ghCopyCode;
    if (!code) return;

    try {
      await this.copyText(code);
      const copiedLabel = copyButton.dataset.copiedLabel || 'Copied';
      if (this.copyStatus) this.copyStatus.textContent = copiedLabel;
      copyButton.setAttribute('data-copied', 'true');
      window.setTimeout(() => copyButton.removeAttribute('data-copied'), 1600);
    } catch (_error) {
      if (this.copyStatus) this.copyStatus.textContent = code;
    }
  }

  async copyText(text) {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text);
    }

    const input = document.createElement('textarea');
    input.value = text;
    input.setAttribute('readonly', '');
    input.style.position = 'fixed';
    input.style.opacity = '0';
    document.body.appendChild(input);
    input.select();
    const copied = document.execCommand('copy');
    input.remove();
    if (!copied) throw new Error('Copy failed');
  }
}

const formatInrPrice = (cents) => {
  const value = Number(cents) / 100;
  if (!Number.isFinite(value)) return '';
  const formatted = new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: value % 1 === 0 ? 0 : 2,
    minimumFractionDigits: 0,
  }).format(value);
  return `₹ ${formatted}`;
};

const toRupeeText = (value) => {
  const text = String(value || '').trim();
  if (!text) return text;
  const amount = text
    .replace(/₹/g, '')
    .replace(/Rs\.?/gi, '')
    .replace(/\bINR\b/gi, '')
    .trim();
  return amount ? `₹ ${amount}` : text;
};

customElements.whenDefined('product-price-element').then(() => {
  const ProductPrice = customElements.get('product-price-element');
  if (!ProductPrice || ProductPrice.prototype.__ghRupeePatched) return;

  const originalUpdatePriceName = ProductPrice.prototype.updatePriceName;
  ProductPrice.prototype.__ghRupeePatched = true;
  ProductPrice.prototype.updatePriceName = function (element, cents) {
    const currency = document.querySelector('.layout--viewport')?.getAttribute('data-currency-code') || 'INR';
    if (currency && currency !== 'INR') {
      originalUpdatePriceName?.call(this, element, cents);
      return;
    }

    const formatted = Number.isFinite(Number(cents)) ? formatInrPrice(cents) : toRupeeText(element?.textContent);
    if (!element || !formatted) return;

    const previous = element.textContent.trim();
    const label = element.getAttribute('aria-label') || '';
    element.innerHTML = formatted;
    if (!label) return;
    element.setAttribute('aria-label', previous && label.endsWith(previous) ? `${label.slice(0, -previous.length).trim()} ${formatted}` : formatted);
  };
});

if (!customElements.get('gh-product-gallery')) {
  customElements.define('gh-product-gallery', GhProductGallery);
}

if (!customElements.get('gh-product-offers')) {
  customElements.define('gh-product-offers', GhProductOffers);
}

customElements.whenDefined('product-carousel').then(() => {
  document.querySelectorAll('.main-product--root[data-gh-design-enabled="true"] product-carousel').forEach((carousel) => {
    if (carousel.dataset.ghArrowLoop === 'true') return;
    carousel.dataset.ghArrowLoop = 'true';

    const previous = carousel.nav?.querySelector('.carousel-nav-arrow--prev');
    const next = carousel.nav?.querySelector('.carousel-nav-arrow--next');
    if (!previous || !next) return;

    const enableArrows = () => {
      if (carousel.totalSlides < 2) return;
      [previous, next].forEach((button) => {
        button.setAttribute('aria-disabled', 'false');
        button.setAttribute('tabindex', '0');
      });
    };

    const checkStartEnd = carousel.checkStartEnd.bind(carousel);
    carousel.checkStartEnd = () => {
      checkStartEnd();
      enableArrows();
    };
    enableArrows();

    previous.addEventListener('click', (event) => {
      if (carousel.totalSlides < 2 || carousel.active_slide !== 1) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      carousel.updateThenGoToActiveSlide(carousel.totalSlides, true);
    }, true);

    next.addEventListener('click', (event) => {
      if (carousel.totalSlides < 2 || carousel.active_slide !== carousel.totalSlides) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      carousel.updateThenGoToActiveSlide(1, true);
    }, true);
  });
});
})();
